import { v4 as uuid } from "uuid";
import { StopwatchSession, stopwatch } from "../utilities/types";
import { SessionsForApi } from "../context/SyncManager";
import api from "../utilities/fetching";

const STORAGE_KEY = "stopwatchSessions";

const stopwatchRepository = {
  /**
   * Gets ALL stopwatch sessions from localStorage.
   */
  getAllSessions: (): StopwatchSession[] => {
    const localData = localStorage.getItem(STORAGE_KEY);
    return localData ? JSON.parse(localData) : [];
  },

  fetchActiveSessions: async (): Promise<stopwatch[]> => {
    const response = await api.get<{ data: stopwatch[] }>("stopwatch/active");
    return response.data;
  },

  fetchStatusesByIds: async (clientIds: string[]): Promise<stopwatch[]> => {
    if (clientIds.length === 0) {
      return []; // Return empty array if no client IDs are provided
    }
    const response = await api.post<{ data: stopwatch[] }>(
      "stopwatches/status-check",
      {
        client_ids: clientIds,
      },
    );
    return response.data; // Assuming your API returns data in this shape
  },

  async syncState(knownIds: string[]) {
    const response = await api.post<{ data: stopwatch[] }>(
      "stopwatches/sync-state",
      {
        known_ids: knownIds,
      },
    );
    return response.data;
  },

  /**
   * Saves the entire array of ALL sessions back to localStorage.
   * @param sessions The full array of stopwatch sessions.
   */
  saveStopwatchSessions: (sessions: StopwatchSession[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  },

  /**
   * Creates a new stopwatch session object in a 'stopped' state.
   * It does NOT save it, that's the job of the hook's `useEffect`.
   * @param tableId The ID of the table this session belongs to.
   */
  createStopwatchSession: (tableId: number): StopwatchSession => {
    const newSession: StopwatchSession = {
      id: uuid(),
      tableId: tableId,
      state: "stopped",
      elapsedTime: 0,
      synced: false,
      lastStartTime: undefined, // Initialize lastStartTime as undefined
    };
    return newSession;
  },

  sync: async (session: SessionsForApi[]) => {
    await api.post("stopwatch/sync", { sessions: session });
  },

  clearAllSessions: () => {
    localStorage.removeItem(STORAGE_KEY);
  },
};

export default stopwatchRepository;
